<template>
  <div v-if="l_visible">
    <v-text-field
      ref="control"
      class="dynamic-input-control"
      :data-id="dataId"
      :dense="dense"
      :type="type"
      :hide-details="hideDetails"
      outlined
      :multiple="multiple"
      :placeholder="placeholder"
      :menu-props="menuProps"
      :loading="isLoading || loading"
      :rules="ruleArray"
      v-model="stringValue"
      :suffix="suffix"
      @focusout="onFocusOut"
      @focus="onFocus"
      @mousedown="handleMouseDown"
      :append-icon="getDisabled || isLoading || readonly ? '' : appendIcon"
      v-bind:class="{
        validated: displayValidated && !hasError && !errorMessages,
        required: l_required && !hideRequired,
        'no-label': !label,
        'text-right': textRight,
        'hide-label': hideLabel,
        naked,
        readonly: readonly || getDisabled,
      }"
      :error-messages="errorMessages"
      :disabled="getDisabled || isLoading || readonly"
    >
      <template v-slot:label>
        {{ getLabel }}
        <span v-if="optional" class="optional">(nepovinné)</span>
      </template>
      <template v-slot:append>
        <slot name="append" v-if="getDisabled || isLoading || readonly ? false : true"></slot>
        <v-tooltip v-if="!!getTooltip && !hideTooltip" bottom max-width="350">
          <template v-slot:activator="{ on }">
            <v-icon slot="activator" class="btn-help text-body-2" v-on="on">mdi-help-circle</v-icon>
          </template>
          <span> {{ getTooltip }} </span>
        </v-tooltip>
      </template>
    </v-text-field>
  </div>
</template>

<script>
import protectedAxios from '../../auth/axios.js';
import { dynamicControlMixin } from '../../mixins/dynamicControlMixin';

export default {
  mixins: [dynamicControlMixin],
  props: {
    value: [String, Number],
    dense: Boolean,
    naked: Boolean,
    multiple: Boolean,
    menuProps: Object,
    placeholder: String,
    source: String,
    optional: Boolean,
    suffix: String,
    appendIcon: String,
    url: Boolean,
    max: Number,
    type: String,
    textRight: Boolean,
    //rules
    email: Boolean,
    rodneCislo: Boolean,
    textOnFocus: String,
    disabled: Boolean,
    removeCharacters: { type: RegExp, default: null },
    isPhoneNumber: { type: Boolean, default: false },
    hideRequired: { type: Boolean, default: false },
    minValue: {
      type: Number,
      default: null
    },
    maxValue: {
      type: Number,
      default: null
    }
  },
  watch: {
    value(val) {
      if (val != null && val != '') {
        if (this.removeCharacters != null) val = val?.replace(this.removeCharacters, '');
        if (this.isPhoneNumber) {
          let firstCharacter = val.slice(0, 1);
          let splitVal = val.split('+').join('');
          val = firstCharacter + splitVal;
        }
      }
      if (String(val) !== this.stringValue) {
        this.stringValue = this.getString(val);
        this.update();
      }
    },
    rodneCislo(v) {
      this.jeRodneCislo = v;
    },
    stringValue(newValue,oldValue){
      if(!this.isLoading && !this.ignoreCompare && newValue != oldValue){
        this.setDirty(true);
      }
    }
  },
  data() {
    return {
      stringValue: this.getString(this.value),
      jeRodneCislo: this.rodneCislo,
    };
  },
  mounted() {
    if (this.removeCharacters != null || this.isPhoneNumber) {
      this.$watch(
        () => {
          return this.$refs.control.lazyValue;
        },
        () => {
          if (this.$refs.control.lazyValue != null && this.$refs.control.lazyValue != '') {
            if (this.removeCharacters != null)
              this.$refs.control.lazyValue = this.$refs.control.lazyValue?.replace(
                this.removeCharacters,
                ''
              );
            if (this.isPhoneNumber) {
              let firstCharacter = this.$refs.control.lazyValue.slice(0, 1);
              let splitVal = this.$refs.control.lazyValue.split('+').join('');
              this.$refs.control.lazyValue = firstCharacter + splitVal;
            }
            if (this.$refs.control.lazyValue.localeCompare(this.stringValue)) {
              this.stringValue = this.$refs.control.lazyValue;
            }
          }
        }
      );
    }
  },
  computed: {
    ruleArray() {
      const r = this.getRules;

      if (this.jeRodneCislo) r.push(this.rodneCisloRule);

      return r;
    },
    getDisabled() {
      return this.disabled || this.isDisabled;
    },
    getMinValue() {
      return this.minValue ?? this.l_minValue;
    },
    getMaxValue() {
      return this.maxValue ?? this.l_maxValue;
    },
  },
  methods: {
    onFocus() {
      if (!this.stringValue && this.textOnFocus) {
        this.stringValue = this.textOnFocus;
      }
    },
    setValue(val) {
      this.stringValue = val;
      this.setDirty(true);
      this.update();
    },
    onFocusOut(val) {
      this.$emit('focusout');
      if(this.ignoreCompare){
        this.setDirty(true);
      }
      this.update();
    },
    readData(config) {
      if (config.controlValue || config.controlValue === 0) {
        this.stringValue = config.controlValue;
        this.$emit('input', this.stringValue);
      } else if (this.stringValue) this.stringValue = null;
    },
    update() {
      if (this.$refs.control?.validate() === true) this.$emit('input', this.stringValue);

      if ((!this.hasError || this.saveLoadError) && this.dirty) {
        return this.saveValue(this.stringValue);
      }

      return Promise.resolve();
    },
    checkMaxCharacters(v) {
      if (this.max && v && v.length > this.max) {
        this.stringValue = v.slice(0, 12);
        return true;
      }
      return true;
    },
    clear() {
      this.stringValue = null;
      this.$emit('input', this.stringValue);
      return this.saveValue(this.stringValue, true);
    },
    rodneCisloRule(v) {
      if (!this.jeRodneCislo) return true;
      if (!v) return true;
      if (v.length === 9 && /^[0-9]{6}[0-9]{3}$/.exec(v) && v.substring(0, 2) < 54) return true;
      if (v.length === 10 && /^[0-9]{6}[0-9]{4}$/.exec(v) && v % 11 === 0) return true;
      else return 'Zadané rodné číslo je nesprávne!';
    },
    emailRule(v) {
      if (!this.email) return true;
      if (!v) return true;
      if (
        //podla RFC 5322
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
          v
        )
      )
        return true;
      else return 'Nesprávny formát e-mailovej adresy';
    },
    getString(val) {
      if (val !== 0 && !val) return null;
      else return String(val);
    },
  },
  created() {
    if (this.email) this.addRule(this.emailRule);

    if (this.jeRodneCislo) this.addRule(this.rodneCisloRule);

    if (this.psc) this.addRule(this.pscRule);

    this.addRule(
      v =>
        /^(https?):\/\/([a-zA-Z0-9.-]+(:[a-zA-Z0-9.&%$-]+)*@)*((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9][0-9]?)(\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])){3}|([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.([a-zA-Z]{2}))(:[0-9]+)*(\/($|[a-zA-Z0-9.,?'\\+&%$#=~_-]+))*$/.test(
          v
        ) ||
        !this.url ||
        !v ||
        'Nesprávny formát URL adresy'
    );

    this.addRule(v =>
      v == null ||
      v === '' ||
      this.getMinValue == null ||
      v.length >= Number(this.getMinValue) ||
      'Hodnota musí obsahovať aspoň ' + this.getMinValue + ' znakov.'
    );

    this.addRule(v =>
      v == null ||
      v === '' ||
      this.getMaxValue == null ||
      v.length <= Number(this.getMaxValue) ||
      'Hodnota nesmie obsahovať viac ako ' + this.getMaxValue + ' znakov.'
    );

    this.addRule(v => this.checkMaxCharacters(v));
  },
};
</script>
